import { useEffect, useRef, useState } from 'react';
import { Card, Modal, TextField, Button, FormLabel, FormControl, InputLabel, Select, MenuItem } from '@mui/material';
import Header from '../../components/Header';
import HeaderImage from '../../images/header_bg_3.jpg';
import { Cancel, Facebook, Instagram, Twitter, WhatsApp } from '@mui/icons-material';
import { Link, useParams } from 'react-router-dom';
import axiosInstance from '../../utils/axiosInstance';
import { toast } from 'sonner';

const Profile = () => {
    const { ID } = useParams();
    const auth = localStorage.getItem('gym_user') ? JSON.parse(localStorage.getItem('gym_user')) : null;
    const canEdit = auth && ID === auth.instructor_id;
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [instructor, setInstructor] = useState({});
    const [editInstructor, setEditInstructor] = useState({});
    const [schedules, setSchedules] = useState([]);
    const [isEditModalOpen, setIsEditModalOpen] = useState(false);
    const [isEditProfileOPen, setIsEditProfileOpen] = useState(false)
    const [newImageUrl, setNewImageUrl] = useState({});
    const [trained, setTrained] = useState([{
        name: '',
        role: '',
        trainee_id: '',
        image: '',
        phone: '',
        email: ''
    }]);
    const [viewOpen, setViewOpen] = useState(false)
    const [isTrainedModalOpen, setIsTrainedModalOpen] = useState(false)
    const [selectedTrainee, setSelectedTrainee] = useState('')
    const [participants, setParticipants] = useState([{
        role: '',
        name: '',
        participant_id: ''
    }]);

    const [booking, setBooking] = useState({
        date: '',
        start_time: '',
        end_time: '',
        duration: '',
        instructor_id: ID,
        user_id: auth?.guest_id ?? auth?.member_id ?? 0
    });

    const [bookings, setBookings] = useState([{
        booking_id: "",
        user_id: "",
        instructor_id: "",
        date: "",
        start_time: "",
        end_time: "",
        duration: "",
        instructor_name: "",
        instructor_email: "",
        instructor_phone: "",
        user_type: "",
        user_name: "",
        user_email: "",
        user_phone: ""
    }])

    const [selectedBooking, setSelectedBooking] = useState({
        booking_id: "",
        user_id: "",
        instructor_id: "",
        date: "",
        start_time: "",
        end_time: "",
        duration: "",
        instructor_name: "",
        instructor_email: "",
        instructor_phone: "",
        user_type: "",
        user_name: "",
        user_email: "",
        user_phone: ""
    })
    const getBookingsRef = useRef()
    const getTrainerRef = useRef()

    const handleView = (booking) => {
        setViewOpen(true)
        setSelectedBooking(booking);
    }

    const calculateDuration = (start, end) => {
        const [startHour, startMinute] = start.split(":").map(Number);
        const [endHour, endMinute] = end.split(":").map(Number);

        const startDate = new Date(0, 0, 0, startHour, startMinute);
        const endDate = new Date(0, 0, 0, endHour, endMinute);

        if (endDate < startDate) {
            endDate.setDate(endDate.getDate() + 1);
        }

        const diffInMs = endDate - startDate;
        const diffInMinutes = Math.floor(diffInMs / (1000 * 60));
        const hours = Math.floor(diffInMinutes / 60);
        const minutes = diffInMinutes % 60;

        return `${hours}h ${minutes}m`;
    };

    const handleEditClick = () => {
        setIsEditModalOpen(true);
    };

    const handleModalClose = () => {
        setIsEditModalOpen(false);
    };

    const handleOpen = () => setIsModalOpen(true);
    const handleClose = () => {
        setIsModalOpen(false);
        if(getBookingsRef.current) getBookingsRef.current();
    }

    const handleOpenTrained = () => setIsTrainedModalOpen(true)

    const bookSession = async () => {
        setBooking((prev) => ({...prev, duration: calculateDuration(booking.start_time, booking.end_time)}))

        await axiosInstance.post(`bookings/create`, booking, {
            headers: { 'Content-Type': 'application/json' }
        }).then(() => {
            toast.success(`Success, successfully booked a session.`)
            setTimeout(() => {
                handleClose();
            }, 2000)
        }).catch((error) => {
            if(error.response && error.response.data.error) {
                toast.error(`Failure, ${error.response.data.error}`)
            } else {
                toast.error(`Failure, ${error}`)
            }
        })
    }

    const handleImageUpdate = async () => {
        if (!newImageUrl || !newImageUrl.name) {
            toast.info("Info! Please select an image and submit or cancel if you don't want to change the image");
            return;
        }

        // Validate file type
        const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png'];
        if (!allowedTypes.includes(newImageUrl.type)) {
            toast.error("Failure! Only .jpg, .jpeg, and .png files are allowed.");
            return;
        }

        const formData = new FormData();
        formData.append('image_url', newImageUrl);

        try {
            await axiosInstance.put(`instructors/update_profile/${ID}`, formData);
            toast.success(`Success, successfully edited ${auth.name}`);
            setTimeout(() => {
                if (getTrainerRef.current) getTrainerRef.current();
                setIsEditModalOpen(false);
            }, 2000);
        } catch (error) {
            const errorMsg = error.response?.data?.error || error.toString();
            toast.error(`Failure, ${errorMsg}`);
        }
    };

    const handleEditTrainer = async () => {
        if(!canEdit) {
            toast.info("Info! You are not authorized to edit this trainer's profile")
            return;
        }

        await axiosInstance.put(`instructors/update/${ID}`, editInstructor).then(() => {
            toast.success(`Success, successfully edited ${editInstructor.name}`)
            setTimeout(() => {
                if (getTrainerRef.current) getTrainerRef.current();
                setIsEditProfileOpen(false);
            }, 2000)
        }).catch((error) => {
            if(error.status === 400) {
                toast.error('Failure! Try again later')
            } else if(error.status === 404) {
                toast.error('Failure! This trainer does not exist')
            } else if(error.status === 500) {
                toast.error('Failure! System under maintenance! Try again later')
            } else if(error.status === 401) {
                toast.error('Failure! You are not allowed to edit this trainer.')
            }
        })
    }

    const markTrained = async () => {
        const payload = {
            client_id: selectedTrainee
        }

        await axiosInstance.post(`instructors/add_to_trained/${ID}`, payload, {
            headers: { 'Content-Type': 'application/json' }
        }).then(() => {
            toast.success(`Success, successfully marked as trained.`)
            setTimeout(() => {
                setIsTrainedModalOpen(false);
                if(getBookingsRef.current) getBookingsRef.current();
            }, 2000)
        }).catch((error) => {
            if(error.response && error.response.data.error) {
                toast.error(`Failure, ${error.response.data.error}`)
            } else {
                toast.error(`Failure, ${error}`)
            }
        })
    }

    useEffect(() => {
        const getSchedules = async () => {
            await axiosInstance.get(`instructors/getall/${ID}/schedules`).then((response) => {
                setSchedules(response.data.data)
            }).catch((err) => {
                if(err.status === 404) {
                    setSchedules([])
                } else {
                    setSchedules([])
                }
            })
        }

        const getTrainer = async () => {
            await axiosInstance.get(`instructors/single/${ID}`).then((response) => {
                setInstructor(response.data.instructor);
                setEditInstructor(response.data.instructor)
            }).catch(() => {
                setInstructor({})
                setEditInstructor({})
            })
        }

        const getBookings = async () => {
            await axiosInstance.get(`bookings/instructor/${ID}`).then((response) => {
                setBookings(response.data.bookings);
                console.log(response.data.bookings);
            }).catch(() => {
                setBookings([])
            })
        }


        const getAllTrainees = async () => {
            await axiosInstance.get(`instructors/get_trained_by/${ID}`).then((response) => {
                setTrained(response.data.clients);
            }).catch(() => {
                setTrained([])
            })
        }

        const getParticipants = async () => {
            await axiosInstance.get(`auth/all_users`).then((response) => {
                setParticipants(response.data.users);
            }).catch(() => {
                setParticipants([])
            })
        }

        getBookingsRef.current = getBookings;
        getTrainerRef.current = getTrainer;

        getTrainer();
        getParticipants();
        getSchedules();
        getBookings()
        getAllTrainees();
    }, [ID])

    useEffect(() => {
        const duration = calculateDuration(booking.start_time, booking.end_time)
        setBooking((prev) => ({...prev, duration: duration}))
    }, [booking.start_time, booking.end_time])

    return (
        <div>
            <Header title="Instructor Profile" image={HeaderImage}>
                Doloremque nobis ipsa asperiores. Lorem ipsum dolor sit amet, consectetur adipisicing elit. Odio dolor eius, quod ipsa nostrum a!
            </Header>

            <div className="columns-1 lg:flex gap-8 p-10 lg:px-20 items-start mt-20 pb-0">
                {/* Profile Image Section */}
                <div className="grid grid-cols-1 gap-4 w-full lg:w-[30%] mb-4 text-center">
                    <div className="relative">
                        <img src={instructor.image_url || 'https://www.academyoffitnessprofessionals.com/wp-content/uploads/2014/08/Personal-Trainer.jpg'} alt={instructor.name} className="rounded-full w-72 h-72 md:w-96 md:h-96 shadow-lg object-center mx-auto" />
                        {canEdit && (
                            <button onClick={handleEditClick} className="absolute bottom-10 right-10 bg-blue-600 text-white rounded-full p-2 shadow-md hover:bg-blue-700 w-8 h-8 flex items-center justify-center">
                                🖉
                            </button>
                        )}
                    </div>
                    <div className='w-full mx-auto mt-8'>
                        {auth && auth.instructor_id !== ID &&
                            <Button variant="contained" color="primary" onClick={handleOpen} className="w-1/2">
                                Book a Session
                            </Button>
                        }
                        {auth && auth.instructor_id === ID &&
                            <Button variant="contained" color="primary" onClick={handleOpenTrained} className="w-1/2">
                                Trained
                            </Button>
                        }
                    </div>
                </div>

                {/* Profile Details Section */}
                <div className="flex flex-col w-full lg:w-[70%] gap-6">
                    <div className="flex flex-wrap w-full gap-6">
                        {/* Social Media Links */}
                        <Card className="p-6 shadow-lg w-full md:w-[33%] lg:w-[36%]">
                            <h2 className="text-lg font-bold text-gray-800 mb-4">Social Media</h2>
                            <ul className="flex flex-wrap gap-4">
                                {instructor.facebook && <li><a href={`https://www.facebook.com/${instructor.facebook}`} target="_blank" rel="noreferrer" className="text-blue-600 flex items-center"><Facebook fontSize='large' /></a></li>}
                                {instructor.twitter && <li><a href={`https://www.twitter.com/${instructor.twitter}`} target="_blank" rel="noreferrer" className="text-blue-400 flex items-center"><Twitter fontSize='large' /></a></li>}
                                {instructor.instagram && <li><a href={`https://www.instagram.com/${instructor.instagram}`} target="_blank" rel="noreferrer" className="text-pink-500 flex items-center"><Instagram fontSize='large' /></a></li>}
                                {instructor.whatsapp && <li><a href={`https://www.whatsapp.com/${instructor.whatsapp}`} target="_blank" rel="noreferrer" className="flex items-center"><WhatsApp color='green' fontSize='large' /></a></li>}
                            </ul>
                            <div className="bg-gray-50 p-4 rounded-md shadow-sm mt-4 w-full text-sm">
                                <div className="flex items-center mb-2 gap-2">
                                    <span className="text-gray-600 font-semibold">Tel:</span>
                                    <a href={`tel:${instructor.phone}`} className="text-blue-500 hover:text-blue-700 transition-colors">
                                        {instructor.phone}
                                    </a>
                                </div>
                                <div className="flex items-center gap-2">
                                    <span className="text-gray-600 font-semibold">Email:</span>
                                    <a href={`mailto:${instructor.email}`} className="text-blue-500 hover:text-blue-700 transition-colors">
                                        {instructor.email}
                                    </a>
                                </div>
                            </div>
                        </Card>

                        {/* Instructor Details */}
                        <Card className="p-6 shadow-lg w-full md:w-[63%] lg:w-[54%]">
                            <div className='flex justify-between'>
                                <h2 className="text-lg font-bold text-gray-800 mb-2">Instructor Details</h2>

                                {canEdit &&
                                    <button className="text-blue-600 p-2 shadow-md font-extrabold" onClick={() => setIsEditProfileOpen(true)}>
                                        🖉
                                    </button>
                                }
                            </div>
                            <div className="text-gray-700 space-y-1">
                                <p><strong>Name:</strong> {instructor.name}</p>
                                {/* <p><strong>Age:</strong> 35</p> */}
                                <p><strong>Bio:</strong> {instructor.bio}</p>
                                <p><strong>Type of Exercises:</strong> {instructor.speciality}</p>
                                <p><strong>Available Times:</strong> {instructor.avialability}</p>
                            </div>
                        </Card>
                    </div>

                    {/* Trainer schedule */}
                    <div className="p-6 w-full px-0">
                        <h2 className="text-lg font-bold mb-4">Trainer Schedule</h2>
                        {schedules && schedules.length > 0 ?
                            <ul className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                {schedules.map((schedule, index) =>
                                    <li key={index} className="flex justify-between items-center bg-gray-200 p-4 rounded-md shadow-sm">
                                        <span className="text-gray-700 font-medium">{schedule.day} - {schedule.exercise}</span>
                                        <span className="text-gray-500 text-sm">{schedule.start_time} {[12,13,14,15,16,17,18,19,20,21,22,23].includes(schedule.start_time.slice(0,2)) ? 'PM' : 'AM'} - {schedule.end_time} {[12,13,14,15,16,17,18,19,20,21,22,23].includes(schedule.start_time.slice(0,2)) ? 'PM' : 'AM'}</span>
                                    </li>
                                )}
                            </ul>
                        :
                            <div className="flex justify-between items-center bg-gray-200 font-bold text-red-700 p-4 rounded-md shadow-sm">
                                No schedules yet
                            </div>
                        }
                    </div>

                    {/* Trainee Profiles */}
                    <div className="p-6 w-full px-0">
                        <h2 className="text-lg font-bold mb-4">Trained Profiles</h2>
                        {trained?.length > 0 ?
                            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                                {trained.map((profile, index) => (
                                    <div key={index} className="flex flex-col items-center text-center p-4 bg-white shadow-md rounded-lg hover:shadow-xl transition-shadow">
                                        <img
                                            src={profile.image || `https://via.placeholder.com/150?text=Trainee+${index + 1}`}
                                            alt={profile.name}
                                            className="rounded-full w-24 h-24 mb-4"
                                        />
                                        <Link to={`/trainee/${profile.trainee_id}/profile/${profile.role}`}><h3 className="text-gray-800 font-semibold">{profile.name}</h3> - <span>{profile.role.toUpperCase()}</span></Link>
                                        <p className="text-sm text-gray-600">{profile.email}</p>
                                        <p className="text-sm text-gray-600">{profile.phone}</p>
                                    </div>
                                ))}
                            </div>
                        :
                            <div className="flex justify-between items-center bg-gray-200 font-bold text-red-700 p-4 rounded-md shadow-sm">
                                No trainees for him yet.
                            </div>
                        }
                    </div>

                    {/* Trainer schedule */}
                    <div className="p-6 w-full px-0">
                        <h2 className="text-lg font-bold mb-4">Trainer Booking</h2>
                        {bookings.length > 0 ?
                            <ul className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                {bookings.map((booking, index) =>
                                    <li key={index} className="flex justify-between items-center bg-gray-200 p-4 rounded-md shadow-sm cursor-pointer" onClick={() => handleView(booking)}>
                                        <span className="text-gray-700 font-medium">{booking.date.slice(0,10)} ⮚ {booking.instructor_name}</span>
                                        <span className="text-gray-500 text-sm">{booking.start_time.slice(0,5)} {[12,13,14,15,16,17,18,19,20,21,22,23].includes(booking.start_time.slice(0,2)) ? 'PM' : 'AM'} - {booking.end_time.slice(0,5)} {[12,13,14,15,16,17,18,19,20,21,22,23].includes(booking.start_time.slice(0,2)) ? 'PM' : 'AM'}</span>
                                    </li>
                                )}
                            </ul>
                            :
                            <div className="flex justify-between items-center bg-gray-200 font-bold text-red-700 p-4 rounded-md shadow-sm">
                                No bookings yet
                            </div>
                        }
                    </div>
                </div>
            </div>

            {/* Modal */}
            <Modal open={isModalOpen} onClose={handleClose} className='flex items-center justify-center w-full'>
                <div className='bg-white w-[90%] lg:w-[40%] shadow p-8 rounded-sm'>
                    <h2 className='text-blue-950 mb-8'>Book a Session</h2>
                    <form>
                        <FormLabel>Schedule Date</FormLabel>
                        <TextField type='date' fullWidth variant="standard" value={booking.date} onChange={(e) => setBooking((prev) => ({...prev, date: e.target.value}))} style={{ marginBottom: '16px' }} />
                        <FormLabel>Start Time</FormLabel>
                        <TextField type='time' fullWidth variant="standard" value={booking.start_time} onChange={(e) => setBooking((prev) => ({...prev, start_time: e.target.value}))} style={{ marginBottom: '16px' }} />
                        <FormLabel>End Time</FormLabel>
                        <TextField type='time' fullWidth variant="standard" value={booking.end_time} onChange={(e) => setBooking((prev) => ({...prev, end_time: e.target.value}))} style={{ marginBottom: '16px' }} />
                        {/* <FormLabel>Duration in hours</FormLabel> */}
                        {/* <TextField type='duration' label='Duration in hours' value={booking.duration} fullWidth variant="outlined" style={{ marginBottom: '16px' }} /> */}
                        <div className='flex gap-4 mx-auto w-[100%] mt-4'>
                            {auth ?
                                <Button variant="contained" color="primary" onClick={bookSession}>
                                    Book a session with John Doe
                                </Button>
                            :
                                <Button disabled variant="contained" color="primary">
                                    Login first
                                </Button>
                            }
                            <Button variant="contained" color="error" onClick={handleClose} className='w-48'>
                                Cancel
                            </Button>
                        </div>
                    </form>
                </div>
            </Modal>

            {/* View booking Modal */}
            <Modal open={viewOpen} onClose={() => setViewOpen(false)} className='flex justify-center items-center'>
                <div className="bg-white w-[90%] lg:w-[40%] p-8 rounded-md shadow-md mx-auto mt-20 text-blue-950">
                    <h2 className="mb-4 text-blue-950">Your booking</h2>
                    <ul>
                        <li>
                            <strong>Date:</strong> {selectedBooking.date.slice(0,10)}
                        </li>
                        <li>
                            <strong>Start Time:</strong> {selectedBooking.start_time.slice(0,6)} AM
                        </li>
                        <li>
                            <strong>End Time:</strong> {selectedBooking.end_time.slice(0,5)} AM
                        </li>
                        <li>
                            <strong>Instructor:</strong> {selectedBooking.instructor_name}
                        </li>
                        <li>
                            <strong>Email:</strong> {selectedBooking.instructor_email}
                        </li>
                        <li>
                            <strong>Trainee:</strong> {selectedBooking.user_name}
                        </li>
                        <li>
                            <strong>Trainee phone:</strong> {selectedBooking.user_phone}
                        </li>
                        <li>
                            <strong>Trainerr email:</strong> {selectedBooking.user_email}
                        </li>
                        <li>
                            <strong>Trainee type:</strong> {selectedBooking.user_type}
                        </li>                        
                    </ul>
                    
                    <div className="flex justify-end gap-4 mt-8">
                        <Button onClick={() => setViewOpen(false)} variant="outlined" color="secondary">
                            Cancel
                        </Button>
                    </div>
                </div>
            </Modal>

            <Modal open={isTrainedModalOpen} onClose={() => setIsTrainedModalOpen(false)} className='flex justify-center items-center'>
                <div className="bg-white w-[90%] lg:w-[40%] p-8 rounded-md shadow-md mx-auto mt-20 text-blue-950">
                    {auth ?
                        <>
                            <h2 className="mb-4 text-blue-950">Mark trainee</h2>
                            <form>
                                <FormControl fullWidth style={{ marginBottom: 15 }}>
                                    <InputLabel>Client</InputLabel>
                                    <Select label="Select client" value={selectedTrainee} onChange={(e) => setSelectedTrainee(e.target.value)}>
                                        {participants.map((participant, index) =>
                                            <MenuItem key={index} value={participant.guest_id || participant.member_id}>
                                                {participant.name} - <span className="text-blue-700 pr-2">{participant.role.toLocaleUpperCase()}</span>
                                            </MenuItem>
                                        )}
                                    </Select>
                                </FormControl>
                                <div className='flex gap-4 mx-auto w-[100%] mt-4 justify-end'>
                                    <Button variant="contained" color="primary" onClick={markTrained}>
                                        Mark as trained
                                    </Button>
                                    <Button variant="contained" color="error" onClick={handleClose} className='w-48'>
                                        Cancel
                                    </Button>
                                </div>
                            </form>
                        </>
                    :
                        <div className="text-center text-blue-950">
                            <div className="text-center flex justify-end text-blue-950">
                            <Button color="error" onClick={() => setIsTrainedModalOpen(false)} className='mt-4'>
                                <Cancel />
                            </Button>
                            </div>
                            <p className='w-full p-8'>Login or create an account(if you don't have any) first to subscribe.</p>
                        </div>
                    }
                </div>
            </Modal>

            {/* Edit Image Modal */}
            <Modal open={isEditModalOpen} onClose={handleModalClose} className='flex justify-center items-center'>
                <div className="bg-white w-[90%] lg:w-[40%] p-8 rounded-md shadow-md mx-auto mt-20">
                    <h2 className="text-blue-950 mb-4">Edit Profile Image</h2>
                    <FormLabel>Image</FormLabel>
                    <TextField fullWidth type='file' variant="outlined" onChange={(e) => setNewImageUrl(e.target.files[0])} className="mb-4" inputProps={{ accept: ".jpg, .jpeg, .png" }} />
                    <div className="flex justify-end gap-4 mt-8">
                        <Button onClick={handleModalClose} variant="outlined" color="secondary">
                            Cancel
                        </Button>
                        <Button onClick={handleImageUpdate} variant="contained" color="primary">
                            Save
                        </Button>
                    </div>
                </div>
            </Modal>

            <Modal open={isEditProfileOPen} onClose={() => setIsEditProfileOpen(false)} className="flex justify-center items-center">
                <div className="bg-white w-[90%] lg:w-[50%] p-8 rounded-md shadow-md mx-auto mt-20 text-gray-900 h-[90%] overflow-y-auto">
                    <h2 className="text-blue-950 mb-4">Edit Profile</h2>
                    <TextField fullWidth type='text' variant="outlined" label="Edit name" className="mb-4" style={{ marginBottom: 15 }} value={editInstructor.name} onChange={(e) => setEditInstructor((prev) => ({...prev, name: e.target.value}))} />
                    <TextField fullWidth type='email' variant="outlined" label="Edit email" className="mb-4" style={{ marginBottom: 15 }} value={editInstructor.email} onChange={(e) => setEditInstructor((prev) => ({...prev, email: e.target.value}))} />
                    <TextField fullWidth type='tel' variant="outlined" label="Edit phone" className="mb-4" style={{ marginBottom: 15 }} value={editInstructor.phone} onChange={(e) => setEditInstructor((prev) => ({...prev, phone: e.target.value}))} />
                    <TextField fullWidth type='text' variant="outlined" label="Edit speciality" className="mb-4" style={{ marginBottom: 15 }} value={editInstructor.speciality} onChange={(e) => setEditInstructor((prev) => ({...prev, speciality: e.target.value}))} />
                    <TextField fullWidth type='text' variant="outlined" label="Edit bio" className="mb-4" style={{ marginBottom: 15 }} multiline value={editInstructor.bio} onChange={(e) => setEditInstructor((prev) => ({...prev, bio: e.target.value}))} />
                    <TextField fullWidth type='text' variant="outlined" label="Edit avialability" className="mb-4" style={{ marginBottom: 15 }} value={editInstructor.avialability} onChange={(e) => setEditInstructor((prev) => ({...prev, avialability: e.target.value}))} />
                    <TextField fullWidth type='number' variant="outlined" label="Edit hourly rate" className="mb-4" style={{ marginBottom: 15 }} value={editInstructor.hourly_rate} onChange={(e) => setEditInstructor((prev) => ({...prev, hourly_rate: e.target.value}))} />
                    <TextField fullWidth type='text' variant="outlined" label="Edit instagram" className="mb-4" style={{ marginBottom: 15 }} value={editInstructor.instagram} onChange={(e) => setEditInstructor((prev) => ({...prev, instagram: e.target.value}))} />
                    <TextField fullWidth type='text' variant="outlined" label="Edit facebook" className="mb-4" style={{ marginBottom: 15 }} value={editInstructor.facebook} onChange={(e) => setEditInstructor((prev) => ({...prev, facebook: e.target.value}))} />
                    <TextField fullWidth type='text' variant="outlined" label="Edit twitter" className="mb-4" style={{ marginBottom: 15 }} value={editInstructor.twitter} onChange={(e) => setEditInstructor((prev) => ({...prev, twitter: e.target.value}))} />
                    <TextField fullWidth type='text' variant="outlined" label="Edit whatsapp" value={editInstructor.whatsapp} onChange={(e) => setEditInstructor((prev) => ({...prev, whatsapp: e.target.value}))} />
                    <div className="flex justify-end gap-4 mt-8">
                        <Button variant="outlined" color="error" onClick={() => setIsEditProfileOpen(false)}>Close</Button>
                        <Button variant="outlined" color="primary" onClick={handleEditTrainer}>Update</Button>
                    </div>
                </div>
            </Modal>
        </div>
    );
};

export default Profile;
